/**
 * CR-6190: OTIF Override control for a delivered-but-failed PO line.
 *
 * Renders nothing unless the line is overridable (the server computes
 * `can_override_otif`: customer/Helix admin, GRN score >= 100, not already
 * overridden, not a declined supplier change). Clicking opens a
 * required-comment dialog; on confirm it overrides OTIF to pass and notifies
 * the supplier (handled server-side). Ports SP's "Override OTIF Status".
 */
import { useState } from 'react';
import { ShieldCheck, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { purchasingAPI, PurchaseOrderLine } from '../../lib/api';
import { getApiErrorMessage } from '../../utils/errorUtils';

interface OTIFOverrideButtonProps {
  line: PurchaseOrderLine;
  /** Called after a successful override so the parent can refresh. */
  onDone: () => void;
}

export default function OTIFOverrideButton({ line, onDone }: OTIFOverrideButtonProps) {
  const [open, setOpen] = useState(false);
  const [comment, setComment] = useState('');
  const [saving, setSaving] = useState(false);

  if (!line.can_override_otif) return null;

  const submit = async () => {
    if (!comment.trim()) {
      toast.error('Please enter a comment/reason for this change.');
      return;
    }
    try {
      setSaving(true);
      await purchasingAPI.overrideOtif(line.id, { comments: comment.trim() });
      toast.success('OTIF status overridden');
      setOpen(false);
      setComment('');
      onDone();
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to override OTIF status'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setOpen(true)}>
        <ShieldCheck className="h-4 w-4" />
        Override OTIF Status
      </Button>

      <Dialog open={open} onOpenChange={(o) => { if (!saving) setOpen(o); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Override OTIF Status</DialogTitle>
            <DialogDescription>
              Mark this fully-delivered line as OTIF-passed. A comment is required
              and is sent to the supplier as a message.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="otif-override-comment">Comment / reason</Label>
            <Textarea
              id="otif-override-comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Why is this line being overridden?"
              rows={4}
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={submit} disabled={saving || !comment.trim()}>
              {saving && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
              Override
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
